import { FundTradingEntityAssignment } from 'api'

// due to timing issues we are handling on front end back end inconsistencies
// originally we could only add a trading entity once
// so we had the selected boolean property, but now
// due to business requirements we can repeat entities so on first time
//
// - add: first we toggle selected to true, for the rest we copy the object with notional as null
// - edit: it's now done by index as we don't have uniq ids
// - remove: it's now done by index as we don't have uniq ids
// - upload: we keep track of the not selecteds not included in the upload to don't delete them,
//     we put the already entered by the user on the dialog, and finally the new uploaded items

export const createNewAssignments = (newAssignment: FundTradingEntityAssignment) => (
  assignments: FundTradingEntityAssignment[]
): FundTradingEntityAssignment[] => {
  const matches = assignments.filter(a => newAssignment.tradingEntityId === a.tradingEntityId) ?? []

  if (matches.length === 1 && !matches[0].selected) {
    return assignments.map(a => {
      if (a.tradingEntityId === newAssignment.tradingEntityId) {
        return { ...a, selected: true }
      }

      return a
    })
  } else {
    const bookIsTheSame = matches.every(a => a.initiatorBook === matches[0].initiatorBook)
    const strategyIsTheSame = matches.every(a => a.initiatorStrategy === matches[0].initiatorStrategy)

    return [
      ...assignments,
      {
        ...copyAssignment({ ...matches[0] }),
        selected: true,
        ...(bookIsTheSame ? { initiatorBook: matches[0].initiatorBook } : {}),
        ...(strategyIsTheSame ? { initiatorStrategy: matches[0].initiatorStrategy } : {})
      }
    ]
  }
}

export const removeAssignment = (assignment: FundTradingEntityAssignment, idx: number) => (
  assignments: FundTradingEntityAssignment[]
): FundTradingEntityAssignment[] => {
  const matches = assignments.filter(a => assignment.tradingEntityId === a.tradingEntityId)

  if (matches.length === 1) {
    return assignments.map((a, i) => {
      if (i === idx) {
        return {
          ...a,
          selected: false,
          notional: null,
          startConsideration: null,
          endConsideration: null
        }
      }

      return a
    })
  } else {
    return assignments.filter((_, i) => i !== idx)
  }
}

export const updateAssignment = (update: Partial<FundTradingEntityAssignment>, idx: number) => (
  assignments: FundTradingEntityAssignment[]
): FundTradingEntityAssignment[] => {
  return assignments.map((a, i) => {
    if (i === idx) {
      return { ...a, ...update }
    }

    return a
  })
}

export const uploadAssignments = (newAssignments: Partial<FundTradingEntityAssignment>[]) => (
  existingAssignments: FundTradingEntityAssignment[]
): FundTradingEntityAssignment[] => {
  const notSelecteds = existingAssignments.filter(a => !a.selected && newAssignments.every(n => n?.tradingEntityId !== a.tradingEntityId))

  const selecteds = existingAssignments.filter(a => a.selected)

  return [
    ...notSelecteds,
    ...selecteds,
    ...newAssignments.map(n => {
      const found = existingAssignments.find(e => e.tradingEntityId === n?.tradingEntityId)
      if (found) {
        return { ...found, ...n }
      }
      return {
        ...createEmptyAssignment(),
        ...n
      }
    })
  ]
}

const copyAssignment = (assignment: FundTradingEntityAssignment): FundTradingEntityAssignment => ({
  ...assignment,
  notional: null,
  startConsideration: null,
  endConsideration: null,
  initiatorBook: null,
  initiatorStrategy: null
})

const createEmptyAssignment = (): FundTradingEntityAssignment => ({
  accruedInterest: null,
  bondPrice: null,
  cleanPrice: null,
  dirtyPrice: null,
  endConsideration: null,
  notional: null,
  selected: null,
  startConsideration: null,
  tradingEntityId: '',
  validationIssues: null,
  initiatorStrategy: null,
  initiatorBook: null
})
